"use client";

import {axiosInstance} from "@/app/api";
import {UserFormConfig, UserViewConfig} from "@/data";
import {TransportationType} from "@/libs/enums/users";
import {mapFields, onGetTableResponse} from "@/libs/helper";

const onGetUser = async (id: string): Promise<any> => {
  const user = UserViewConfig;
  return await axiosInstance()
    .get(`/api/users/read?userid=${id}`)
    .then((response: any) => {
      if (response.data.status === "success") {
        const neoresponse: NeoResponse<IUserView> = response.data;
        if (neoresponse && neoresponse.data) {
          neoresponse.data.rfid = neoresponse.data.rfid == true ? "Yes" : "No";

          const pickup = neoresponse.data.vehicles
            .filter(
              item =>
                item.type === TransportationType.Pickup ||
                item.type === TransportationType.Both
            )
            .map(x => x.trackerName);
          const drop = neoresponse.data?.vehicles
            .filter(
              item =>
                item.type === TransportationType.Drop ||
                item.type === TransportationType.Both
            )
            .map(x => x.trackerName);
          neoresponse.data.pickup = pickup;
          neoresponse.data.drop = drop;
          const userType = neoresponse.data?.userType
            ? neoresponse.data?.userType === "Parent" ||
              neoresponse.data?.userType === "Student"
              ? "student"
              : neoresponse.data?.userType
            : neoresponse.data?.className
              ? "student"
              : "employee";
          const cards = user.cards.filter(
            x => x.type == userType || x.type == "user"
          );
          const imageUrl = neoresponse.data.image;
          user.cards = cards.map(card => ({
            ...card,
            fields: mapFields([neoresponse.data], card.fields as TNeoField[]),
            imageUrl: imageUrl
          }));
        }
        return user;
      } else {
        return user;
      }
    })
    .finally(() => {
      return user;
    });
};

const onFetchUserDetails = async (
  id: string,
  action: string,
  slug: string
): Promise<any> => {
  const formView = UserFormConfig;
  if (action === "create") {
    return {
      userId: "",
      data: slug === "user" ? {} : slug === "employee" ? {} : {}
    };
  }
  if (slug === "staff") {
    return await onFetchStaffDetails(id);
  }
  return await axiosInstance()
    .get(`/api/users/read?userid=${id}`)
    .then((response: any) => {
      const data = response.data.data;
      if (response.data.status === "success") {
        data.gender =
          data.gender.toLowerCase() === "male"
            ? "1"
            : data.gender.toLowerCase() === "female"
              ? "2"
              : "";
        if (slug === "user") {
          formView.user.name = data?.name;
          formView.user.dob = data?.dob;
          formView.user.gender = data?.gender;
          formView.user.parentName = data?.parentName;
          formView.user.phone = data?.userPhone;
          formView.user.email = data?.email;
          formView.user.emergencyContactNo = data?.contactNo;
          formView.user.destination = data?.stop;
          formView.user.rfidNo = data?.rfidNo;
          formView.user.rfid = data?.rfid;
          formView.user.uhfId = data?.uhfId;
          formView.user.uhf = data?.uhf;
          formView.user.userRole = data?.role;
          formView.user.userType = data?.type;
          formView.user.admissionNo = data?.admissionNo;
          formView.user.className = data?.className;
          formView.user.sectionName = data?.sectionName;
          formView.user.secondaryNumber = data?.secondaryNumber;
          formView.user.profileImage = data?.image;
          formView.user.empStopName = data?.empStopName;
          formView.user.empCategoryName = data?.empCategoryName;
          formView.user.empCategory = data?.empCategoryId
            ? String(data.empCategoryId)
            : "";
          formView.user.hasBp = data?.hasBp;
          formView.user.isTransport = data?.transport === 1 ? "1" : "0";
          formView.user.vehicles = data?.vehicles;
          formView.user.pickup = data?.vehicles?.filter(
            (vehicle: any) =>
              vehicle.type == TransportationType.Pickup ||
              vehicle.type == TransportationType.Both ||
              vehicle.type == 0
          );
          formView.user.drop = data?.vehicles?.filter(
            (vehicle: any) =>
              vehicle.type == TransportationType.Drop ||
              vehicle.type == TransportationType.Both ||
              vehicle.type == 0
          );
          return {
            userId: data?.userId,
            data: formView.user
          };
        } else if (slug === "student") {
          formView.student.userId = data?.userId;
          formView.student.studentName = data?.name;
          formView.student.dob = data?.dob;
          formView.student.studentId = data?.studentId;
          formView.student.className = data?.className;
          formView.student.sectionName = data?.sectionName;
          formView.student.admissionNo = data?.admissionNo;
          formView.student.pickup = data?.vehicles?.filter(
            (vehicle: any) =>
              vehicle.type == TransportationType.Pickup ||
              vehicle.type == TransportationType.Both
          );
          formView.student.drop = data?.vehicles?.filter(
            (vehicle: any) =>
              vehicle.type == TransportationType.Drop ||
              vehicle.type == TransportationType.Both
          );
          return {
            studentId: data?.studentId,
            data: formView.student
          };
        } else if (slug === "employee") {
          formView.employee.employeeId = data?.employeeId;
          formView.employee.dob = data?.dob;
          formView.employee.empDepartment = data?.empDept;
          formView.employee.destination = data?.stop;
          formView.employee.employeeType = data?.eType;
          formView.employee.agency = data?.agency;
          formView.employee.category = data?.category;
          formView.employee.shift = data?.shift;
          formView.employee.empStopName = data?.empStopName;
          formView.employee.empCategoryName = data?.empCategoryName;
          formView.employee.pickup = data?.vehicles?.filter(
            (vehicle: any) =>
              vehicle.type == TransportationType.Pickup ||
              vehicle.type == TransportationType.Both
          );
          formView.employee.drop = data?.vehicles?.filter(
            (vehicle: any) =>
              vehicle.type == TransportationType.Drop ||
              vehicle.type == TransportationType.Both
          );
          return {
            employeeId: data?.employeeId,
            data: formView.employee
          };
        }
      }
    })
    .catch((error: any) => {
      return {
        userId: id,
        data:
          slug === "user"
            ? formView.user
            : slug === "employee"
              ? formView.employee
              : formView.student
      };
    });
};

const onUpdateUserDetails = async (
  userId: any,
  additionalPrimaryData: any,
  data: any,
  userData: any
): Promise<any> => {
  // arranging body json, because we should send as like web api doc
  /*const body = {
    userId: userId,
    insId: additionalPrimaryData?.insId,
    loginUserId: additionalPrimaryData?.currentUser,
    name: data?.name,
    parentName: data?.parentName,
    phone: data?.phone,
    email: data?.email,
    rfidNo: data?.rfidNo,
    vehicles: data?.vehicles,
    gender: data?.gender,
    emergencyNo: data?.emergencyContactNo,
    rfid: data?.rfid,
    userRole: data?.userRole,
    userType: data?.userType
  };
  return await axiosInstance().put("/api/users/update", body);*/
  return await axiosInstance().post("api/users/update/v2", userData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};
//@surya use types what is this 1,2,3
const onUpdateStudentDetails = async (
  userId: any,
  additionalPrimaryData: any,
  data: any
): Promise<any> => {
  const formatDate = (date: Date | null | undefined): string | null => {
    if (!date) return null;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  // Split the pickup and drop IDs into arrays
  const pickupIds =
    typeof data.pickup === "string" ? data.pickup.split(",") : data.pickup;
  const dropIds =
    typeof data.drop === "string" ? data.drop.split(",") : data.drop;

  // Trim whitespace from each ID
  const cleanedPickupIds = pickupIds.map((id: any) => id.trim());
  const cleanedDropIds = dropIds.map((id: any) => id.trim());

  const combinedIds = Array.from(
    new Set([...cleanedPickupIds, ...cleanedDropIds])
  );

  // Map combined array to form a obj with type
  const trackerObjects = combinedIds.map(trackerid => {
    // to cosider type 3 if same trackerid in pick and drop
    const isPickup = pickupIds.includes(trackerid);
    const isDrop = dropIds.includes(trackerid);

    let type;
    if (isPickup && isDrop) {
      // Tracker ID is present in both pickup and drop
      type = 3;
    } else if (isPickup) {
      // Tracker ID is only present in pickup
      type = 1;
    } else {
      // Tracker ID is only present in drop
      type = 2;
    }

    return {
      trackerid: trackerid,
      type: type
    };
  });

  // arranging body json, because we should send as like web api doc
  const body = {
    userClass: data?.standard,
    userSection: data?.section,
    admissionNo: data?.admissionNo,
    dob: formatDate(data?.dateOfBirth),
    vehicles: trackerObjects,
    userId: userId,
    insId: additionalPrimaryData?.insId,
    loginUserId: additionalPrimaryData?.currentUser
  };
  return await axiosInstance().put("/api/users/update/secondary", body);
};

const onCreateUserDetails = async (
  additionalPrimaryData: any,
  data: any
): Promise<any> => {
  // arranging body json, because we should send as like web api doc
  /*const body = {
    name: data?.name,
    parentName: data?.parentName,
    phone: data?.phoneNumber,
    gender: data?.gender,
    email: data?.email,
    emergencyNo: data?.emergencyContactNo,
    destination: data?.destination,
    rfid: data?.rfid,
    rfidNo: data?.rfidNo,
    userRole: data?.userRole,
    userType: data?.userType,
    loginUserId: additionalPrimaryData?.currentUser, // should change dynamically
    insId: additionalPrimaryData?.insId
  };
  return await axiosInstance().post("api/users/create", body);*/
  return await axiosInstance().post("api/users/create/v2", data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const onFetchStaffDetails = async (id: string): Promise<any> => {
  return await axiosInstance()
    .get(`/api/users/staff/read?userid=${id}`)
    .then((response: any) => {
      const staff =
        response?.data?.status === "success" ? response.data.data?.staff : null;
      return {
        userId: staff?.userId ?? id,
        data: staff ?? {}
      };
    });
};

const onCreateStaffDetails = async (data: FormData): Promise<any> => {
  return await axiosInstance().post("/api/users/staff/create", data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const onUpdateStaffDetails = async (data: FormData): Promise<any> => {
  return await axiosInstance().post("/api/users/staff/update", data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const onGetUsers = async (url: string, body: any): Promise<any> => {
  return await axiosInstance()
    .post(url, body)
    .then((response: any) => {
      if (response?.data?.status === "success") {
        const neoresponse: NeoResponse<IUsers> = response.data;
        const rows = neoresponse?.data?.users?.map(row => ({
          ...row,
          dob: row?.dob ?? "",
          uhfId: row?.uhfId ?? "",
          id: row.userId
        }));
        return {
          columns: neoresponse?.data?.columns,
          rows: rows,
          meta: neoresponse?.meta
        } as TableResponse<Users>;
      }
      return onGetTableResponse();
    })
    .catch((error: any) => {
      return onGetTableResponse();
    });
};

const userMappingOnEdit = async (
  request: UserMappingRequest
): Promise<UserMappingResponse> => {
  return await axiosInstance()
    .post("/api/userMapping", request)
    .then((response: any) => {
      return {
        code: response?.data?.code ?? 500,
        status: response?.data?.status ?? "failure",
        message:
          response?.data?.message || "Vehicle mapping updated unsuccessfully"
      } as UserMappingResponse;
    })
    .catch((error: any) => {
      return {
        code: error?.response?.data?.code ?? 500,
        status: error?.response?.data?.status ?? "failure",
        message:
          error?.response?.data?.message ||
          "Vehicle mapping updated unsuccessfully"
      } as UserMappingResponse;
    });
};

const onGetVehiclesWithBps = async (userId: string): Promise<any[]> => {
  return await axiosInstance()
    .get(`/api/user/vehiclesWithBps?userId=${userId}`)
    .then((response: any) => {
      if (response.data.status === "success") {
        return response.data.data?.reportData ?? [];
      }
      return [];
    })
    .catch(() => {
      return [];
    });
};

const onFindVehiclesNearLocation = async (
  latitude: number,
  longitude: number,
  radius: number,
  tripType: 0 | 1
): Promise<any[]> => {
  return await axiosInstance()
    .post("/api/find/vehicles", {latitude, longitude, radius, tripType})
    .then((response: any) => {
      if (response.data.status === "success") {
        return response.data.data?.reportData ?? [];
      }
      return [];
    })
    .catch(() => {
      return [];
    });
};

type VehicleChangeMappingRequest = {
  existingTrackerId: number;
  newTrackerId: number;
  vehicleTripType: 1 | 2;
  existingRouteId: number;
  newRouteId: number;
  newBpLat: number;
  newBpLng: number;
  newBpId?: number;
};

const onVehicleChange = async (
  userId: string,
  mappingRequests: VehicleChangeMappingRequest[],
  forcedMapping: boolean = true
): Promise<{status: string; message: string}> => {
  return await axiosInstance()
    .post("/api/user/vehicleChange", {forcedMapping, userId, mappingRequests})
    .then((response: any) => ({
      status: response.data?.status ?? "failure",
      message: response.data?.message ?? "Vehicle change applied."
    }))
    .catch((error: any) => ({
      status: "failure",
      message: error?.response?.data?.message ?? "Vehicle change failed."
    }));
};

const onUserAction = async (
  url: string,
  institutionUserId: string | null,
  actionType: string,
  payload: any
): Promise<any> => {
  let body;

  switch (actionType) {
    case "delete":
    case "resetpassword":
      body = {userId: institutionUserId};
      break;
    default:
      body = payload;
      break;
  }
  return await axiosInstance()
    .put(url, body)
    .then((response: any) => {
      return response; // returning status and message
    })
    .catch((error: any) => {
      throw error;
    });
};

const onAddUserComment = async (
  userId: string,
  date: number,
  comments: string,
  type: number = 1
): Promise<any> => {
  const body = {userId, date, comments, type};
  return await axiosInstance()
    .post("/api/users/comments/add", body)
    .then((response: any) => {
      return response?.data;
    })
    .catch((error: any) => {
      throw error;
    });
};
const onUpdateUserStatus = async (
  userId: string,
  active: number,
  comments: string
): Promise<{status: string; message?: string}> => {
  return await axiosInstance()
    .post("/api/users/active/update", {userId, active, comments})
    .then((response: any) => response?.data)
    .catch((error: any) => ({
      status: "failure",
      message: error?.response?.data?.message
    }));
};

const onGetUserStatusHistory = async (userId: string): Promise<any[]> => {
  return await axiosInstance()
    .get("/api/users/active/history", {params: {userid: userId}})
    .then((response: any) => {
      if (response?.data?.status !== "success") return [];
      const rows = response.data.data?.reportData ?? [];
      return (Array.isArray(rows) ? rows : []).map(
        (item: any, index: number) => ({
          id: item.createdDt ?? index,
          status: String(item.active).toLowerCase() === "active",
          comments: item.comments ?? "",
          dateTime: item.createdDt ?? null,
          updatedBy: item.updatedBy ?? ""
        })
      );
    })
    .catch(() => []);
};
const getCategoryList = async (): Promise<{id: number; name: string}[]> => {
  return await axiosInstance()
    .get("/api/employee/category/list")
    .then((response: any) => {
      if (response?.data?.status === "success") {
        return response.data.data.reportData ?? [];
      }
      return [];
    })
    .catch(() => []);
};
const onGetBpHistory = async (
  userId: string
): Promise<{pickup: any[]; drop: any[]}> => {
  return await axiosInstance()
    .post(`/api/user/bp/history`, {userid: userId})
    .then((response: any) => {
      if (response?.data?.status === "success") {
        return {
          pickup: response.data.data.pickup,
          drop: response.data.data.drop
        };
      }
      return {pickup: [], drop: []};
    });
};
export const userService = {
  onGetUsers,
  onGetUser,
  onUpdateUserDetails,
  onFetchUserDetails,
  onUserAction,
  onUpdateStudentDetails,
  onCreateUserDetails,
  onFetchStaffDetails,
  onCreateStaffDetails,
  onUpdateStaffDetails,
  userMappingOnEdit,
  onGetVehiclesWithBps,
  onFindVehiclesNearLocation,
  onVehicleChange,
  onGetBpHistory,
  getCategoryList,
  onAddUserComment,
  onUpdateUserStatus,
  onGetUserStatusHistory
};
